import { createPortal } from 'react-dom'
import { Clock, X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import styles from './SessionActivityDialog.module.css'

export type SessionActivityDialogProps = {
  isOpen: boolean
  title?: string
  message: string
  primaryLabel?: string
  secondaryLabel?: string
  onPrimary: () => void
  onSecondary: () => void
  onDismiss?: () => void
  primaryTestId?: string
  secondaryTestId?: string
  overlayTestId?: string
}

/**
 * Session activity / timeout dialog — centered card with teal session icon,
 * title rule, and Continue / Cancel actions (design reference session modal).
 */
export function SessionActivityDialog({
  isOpen,
  title = 'Session Activity',
  message,
  primaryLabel = 'Continue',
  secondaryLabel = 'Cancel',
  onPrimary,
  onSecondary,
  onDismiss,
  primaryTestId = 'session-activity-continue',
  secondaryTestId = 'session-activity-cancel',
  overlayTestId = 'session-activity-overlay',
}: SessionActivityDialogProps) {
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const handleDismiss = () => {
    if (onDismiss) onDismiss()
    else onSecondary()
  }

  const content = (
    <div
      className={styles.overlay}
      onClick={handleDismiss}
      role="presentation"
      data-testid={overlayTestId}
    >
      <div
        className={styles.panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-activity-title"
        aria-describedby="session-activity-message"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          onClick={handleDismiss}
          aria-label="Close"
          data-testid="session-activity-close"
        >
          <X className={styles.closeIcon} aria-hidden />
        </button>

        <div className={styles.iconWrap} aria-hidden>
          <span className={styles.iconCircle}>
            <Clock className={styles.iconClock} strokeWidth={1.75} />
          </span>
          <span className={styles.iconBadge}>!</span>
        </div>

        <h2 id="session-activity-title" className={styles.title}>
          {title}
        </h2>
        <div className={styles.titleRule} aria-hidden />

        <p id="session-activity-message" className={styles.message}>
          {message}
        </p>

        <div className={styles.actions} role="group" aria-label="Session actions">
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onPrimary}
            data-testid={primaryTestId}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onSecondary}
            data-testid={secondaryTestId}
          >
            {secondaryLabel}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
